import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    FaBold, FaItalic, FaCode, FaHeading,
    FaListUl, FaListOl, FaQuoteRight, FaMinus,
    FaLink, FaTable, FaEye, FaPen, FaPaperclip,
    FaLock
} from 'react-icons/fa';

/**
 * Static toolbar button config — defined at module scope so no ref is ever
 * captured during render. Each entry carries the markdown snippets needed by
 * the shared handleToolbarAction handler.
 */
const TOOLBAR_BUTTONS = [
    { icon: <FaHeading />,    title: 'Heading',         before: '## ',  after: '',       placeholder: 'Heading' },
    { icon: <FaBold />,       title: 'Bold',            before: '**',   after: '**',     placeholder: 'bold text' },
    { icon: <FaItalic />,     title: 'Italic',          before: '_',    after: '_',      placeholder: 'italic text' },
    { icon: <FaCode />,       title: 'Inline code',     before: '`',    after: '`',      placeholder: 'code' },
    { sep: true },
    { icon: <FaListUl />,     title: 'Bullet list',     before: '- ',   after: '',       placeholder: 'item' },
    { icon: <FaListOl />,     title: 'Numbered list',   before: '1. ',  after: '',       placeholder: 'item' },
    { icon: <FaQuoteRight />, title: 'Blockquote',      before: '> ',   after: '',       placeholder: 'quote' },
    { icon: <FaMinus />,      title: 'Horizontal rule', before: '\n---\n', after: '',   placeholder: '' },
    { sep: true },
    { icon: <FaLink />,       title: 'Link',            before: '[',    after: '](url)', placeholder: 'link text' },
    { icon: <FaTable />,      title: 'Table',           before: '\n| Column 1 | Column 2 |\n| --- | --- |\n| ', after: ' | |\n', placeholder: 'data' },
];

/**
 * CommentInputSection Component
 *
 * A full-featured comment composer for Action Items. Supports:
 *  - GitHub-Flavoured Markdown (bold, italic, code, headings, lists, tables, etc.)
 *  - Write / Preview toggle so the author can see how their comment will render
 *  - Markdown toolbar buttons that wrap selected text or insert placeholders
 *  - @mention autocomplete for quick user tagging, with optional Microsoft 365 fallback
 *
 * Props
 * ─────
 * @param {string}   commentText      - Controlled value of the current comment
 * @param {Function} onCommentChange  - Called with a synthetic { target: { value } } event on text change
 * @param {Function} onCommentSubmit  - Called when the user clicks "Add Comment"
 * @param {boolean}  isSubmitting     - Disables controls while the submission request is in flight
 * @param {Array}    allUsers         - Full user list used to resolve @mention suggestions
 * @param {Function} getUserDisplay   - Returns a display name string for a user object
 * @param {Function} [onMentionSearch] - Optional async function `(term: string) => Promise<User[]>` called
 *                                       when the local user list returns no results for a @mention search.
 *                                       Should return M365/Entra user objects with at least { displayName, email }.
 */
const CommentInputSection = ({
    commentText,
    onCommentChange,
    onCommentSubmit,
    isSubmitting,
    allUsers = [],
    getUserDisplay = () => '',
    onFileAttach = null,
    onMentionSearch = null,
    canAddInternalNotes = false,
    onInternalNoteSubmit = null
}) => {
    // 'write' shows the textarea + toolbar; 'preview' renders the markdown output
    const [commentMode, setCommentMode] = useState('write');

    // @mention state — tracks the search term and dropdown visibility
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);

    // M365 / Entra fallback search state — populated only when local allUsers returns no matches
    const [m365MentionUsers, setM365MentionUsers] = useState([]);
    const [isSearchingM365, setIsSearchingM365] = useState(false);

    const textareaRef = useRef(null);
    const suggestionsRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    // Ref to track latest comment text for async file upload callbacks (avoids stale closure).
    // Updated in a useEffect scoped to [commentText] so the effect only re-runs when
    // commentText actually changes. File uploads are user-triggered async operations and
    // always fire after the effect has had a chance to run, so the ref is always
    // up-to-date when any async callback reads it.
    const commentTextRef = useRef(commentText);
    useEffect(() => {
        commentTextRef.current = commentText;
    }, [commentText]); // Re-run only when commentText changes

    // Ref for the M365 debounce timer — allows cancelling a pending search when the
    // user keeps typing. A ref is used (not state) so we don't trigger re-renders when
    // the timer ID changes.
    const m365SearchTimerRef = useRef(null);

    // Generation counter — incremented every time a new M365 search is kicked off.
    // The async callback checks that the counter still matches when it resolves, so
    // any result from an older (stale) query is silently discarded and never applied
    // to the dropdown. This prevents the race condition where a slow first request
    // overwrites the correct result from a faster second request.
    const m365SearchGenerationRef = useRef(0);

    // ----------------------------------------------------------------
    // Markdown toolbar — action handler
    // Uses mouseDown (not click) so the textarea doesn't lose focus.
    // Reads textareaRef.current only when called (event handler context).
    // ----------------------------------------------------------------

    const handleToolbarAction = useCallback((btn) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const { before = '', after = '', placeholder = '' } = btn;
        const start = ta.selectionStart;
        const end   = ta.selectionEnd;
        const selected = commentText.substring(start, end) || placeholder;
        const newText = commentText.substring(0, start) + before + selected + after + commentText.substring(end);
        onCommentChange({ target: { value: newText } });
        setTimeout(() => {
            ta.focus();
            const cursorPos = start + before.length + selected.length + after.length;
            ta.setSelectionRange(cursorPos, cursorPos);
        }, 0);
    }, [commentText, onCommentChange]);


    // ----------------------------------------------------------------
    // File drag-and-drop / paste handling
    // ----------------------------------------------------------------

    /**
     * Determine if a file is an image type that can be rendered inline.
     */
    const isImageFile = (file) => file.type.startsWith('image/');

    /**
     * Escape characters in a filename that would otherwise break Markdown link/image
     * syntax. The backslash itself is escaped first so it cannot smuggle through or
     * neutralise a following escape sequence, then square brackets (which terminate the
     * link text and would split the label) are backslash-escaped.
     */
    const escapeMarkdownLabel = (name) => (name || '').replace(/([\\[\]])/g, '\\$1');

    /**
     * Handle file(s) being dropped or pasted into the comment box.
     * Uploads each file and inserts a markdown reference at the cursor position.
     * Images get ![alt](url) syntax; other files get [name](url) link syntax.
     *
     * The URL returned by onFileAttach is the authenticated app download endpoint
     * (/api/ActionItems/attachments/{id}/download), which is proxied through Microsoft
     * Graph on the server. This means the link opens the file locally for any user with
     * access to the action item — without requiring SharePoint site permissions — and
     * lets the PDF annotation feature resolve the attachment from the link.
     *
     * Uses commentTextRef to always read the latest text, preventing stale closures
     * when multiple files are processed sequentially.
     */
    const handleFilesAttach = useCallback(async (files) => {
        if (!onFileAttach || !files || files.length === 0) return;

        for (const file of files) {
            // Insert a placeholder while uploading — always read latest text from ref
            const placeholder = isImageFile(file)
                ? `![Uploading ${file.name}...](...)`
                : `[Uploading ${file.name}...](...)`;

            const currentText = commentTextRef.current;
            const ta = textareaRef.current;
            const cursorPos = ta ? ta.selectionStart : currentText.length;
            const before = currentText.substring(0, cursorPos);
            const after = currentText.substring(cursorPos);
            const separator = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
            const newText = before + separator + placeholder + '\n' + after;
            onCommentChange({ target: { value: newText } });

            // Upload the file
            const result = await onFileAttach(file);

            // Replace placeholder with actual markdown link using latest text.
            // result.url is the authenticated, Graph-proxied app download endpoint.
            if (result && result.url) {
                const label = escapeMarkdownLabel(file.name);
                const finalMarkdown = isImageFile(file)
                    ? `![${label}](${result.url})`
                    : `[${label}](${result.url})`;

                const latestText = commentTextRef.current;
                onCommentChange({ target: { value: latestText.replace(placeholder, finalMarkdown) } });
            } else {
                // Remove placeholder on failure
                const latestText = commentTextRef.current;
                onCommentChange({ target: { value: latestText.replace(separator + placeholder + '\n', '') } });
            }
        }
    }, [onFileAttach, onCommentChange]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) handleFilesAttach(files);
    }, [handleFilesAttach]);

    const handlePaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const files = [];
        for (const item of items) {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) files.push(file);
            }
        }
        if (files.length > 0) {
            e.preventDefault();
            handleFilesAttach(files);
        }
    }, [handleFilesAttach]);

    // ----------------------------------------------------------------
    // @mention handling
    // ----------------------------------------------------------------

    /**
     * Schedule a debounced Microsoft 365 fallback search for the given term.
     * This is a TRUE fallback — it only fires when the local `allUsers` list has
     * no matches for the current term, preventing unnecessary Graph API calls when
     * local results already satisfy the query.
     *
     * Race-condition safety: a generation counter (`m365SearchGenerationRef`) is
     * incremented each time this function is called. The async callback captures
     * the generation at scheduling time and discards the result if the counter
     * has advanced by the time the promise resolves (i.e., the user kept typing).
     *
     * Called from handleTextChange (event handler, not a useEffect) to comply
     * with the react-hooks/set-state-in-effect ESLint rule.
     *
     * @param {string} term - The @mention search term typed after '@'
     * @param {number} localMatchCount - Number of local user matches for this term
     */
    const scheduleM365Search = (term, localMatchCount) => {
        clearTimeout(m365SearchTimerRef.current);

        if (!onMentionSearch || term.length < 2) {
            // Not enough characters — clear any stale M365 results immediately
            setM365MentionUsers([]);
            setIsSearchingM365(false);
            return;
        }

        // If the local list already has results for this term, don't hit the Graph API.
        // M365 is only a fallback for when the user doesn't exist in CapTrac.
        if (localMatchCount > 0) {
            setM365MentionUsers([]);
            setIsSearchingM365(false);
            return;
        }

        // Increment generation so any in-flight request from the previous term is invalidated
        const currentGeneration = ++m365SearchGenerationRef.current;

        // Debounce: wait 350 ms after the user stops typing before hitting the API
        m365SearchTimerRef.current = setTimeout(async () => {
            setIsSearchingM365(true);
            try {
                const results = await onMentionSearch(term);
                // Discard results if a newer search has already been requested
                if (m365SearchGenerationRef.current !== currentGeneration) return;
                setM365MentionUsers(Array.isArray(results) ? results : []);
            } catch (error) {
                // M365 search failures are non-fatal — fall back silently to local results.
                // Log to console so failures are visible in browser devtools.
                console.error('[CommentInputSection] M365 @mention search failed:', error);
                if (m365SearchGenerationRef.current === currentGeneration) {
                    setM365MentionUsers([]);
                }
            } finally {
                if (m365SearchGenerationRef.current === currentGeneration) {
                    setIsSearchingM365(false);
                }
            }
        }, 350);
    };

    /**
     * Filter the local `allUsers` list by a mention search term.
     * Matches against the user's display name or email address.
     * Used both in `handleTextChange` (to count local matches for the M365 fallback
     * decision) and when building the combined dropdown list (to avoid duplicating
     * this filtering logic in two places).
     *
     * @param {string} term - The @mention search string (NOT lowercased yet)
     * @returns {Array} Filtered subset of `allUsers`
     */
    const filterLocalUsers = (term) => {
        if (!term.trim()) return allUsers.slice(0, 10);
        const lower = term.toLowerCase();
        return allUsers.filter(u =>
            getUserDisplay(u).toLowerCase().includes(lower) ||
            (u.email && u.email.toLowerCase().includes(lower))
        );
    };

    /**
     * Update text in the parent and detect @mention triggers.
     * When the user types @ at the start of a word, show the user suggestion dropdown.
     * Supports both name and email searches (e.g. @john.doe@company.com).
     */
    const handleTextChange = (e) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;
        setCursorPosition(cursorPos);

        const textBeforeCursor = value.substring(0, cursorPos);

        // Find the mention-triggering @ by searching backwards for an @ that is
        // preceded by whitespace, newline, or is at position 0. This avoids
        // picking up the @ in an email address typed as part of the search term.
        let mentionStart = -1;
        for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
            const ch = textBeforeCursor[i];
            if (ch === ' ' || ch === '\n') break; // hit whitespace before finding @
            if (ch === '@') {
                const charBefore = i > 0 ? textBeforeCursor[i - 1] : ' ';
                if (charBefore === ' ' || charBefore === '\n' || i === 0) {
                    mentionStart = i;
                }
                break;
            }
        }

        if (mentionStart !== -1) {
            const searchTerm = textBeforeCursor.substring(mentionStart + 1);
            if (!searchTerm.includes(' ')) {
                setMentionSearch(searchTerm);
                setShowMentionSuggestions(true);
                // Use the shared filterLocalUsers helper to get the count of local
                // matches. scheduleM365Search uses this to skip the Graph API call
                // when local results already cover the query.
                const localCount = filterLocalUsers(searchTerm).length;
                scheduleM365Search(searchTerm, localCount);
            } else {
                setShowMentionSuggestions(false);
                setM365MentionUsers([]);
            }
        } else {
            setShowMentionSuggestions(false);
            setM365MentionUsers([]);
        }

        onCommentChange(e);
    };

    /**
     * Build the combined @mention suggestion list.
     *
     * Priority order:
     *   1. Local CapTrac users matching the search term (always shown first)
     *   2. M365 / Entra users from the async fallback search, with local duplicates
     *      removed (matched by email address, case-insensitive).
     *
     * The combined list is capped at 10 entries so the dropdown stays compact.
     * M365 users are tagged with `_isM365: true` so the dropdown can display a
     * visual hint distinguishing them from local application users.
     *
     * Uses the shared `filterLocalUsers` helper to ensure the filtering logic is
     * identical to what is used in handleTextChange for the M365 fallback decision.
     */
    const localMentionUsers = filterLocalUsers(mentionSearch);

    // Build a set of email addresses already covered by local results to avoid
    // showing the same person twice when they exist in both systems.
    const localEmailSet = new Set(
        localMentionUsers
            .map(u => (u.email || '').toLowerCase())
            .filter(Boolean)
    );

    // M365 results that are not already represented locally
    const uniqueM365Users = m365MentionUsers
        .filter(u => {
            const email = (u.email || u.mail || '').toLowerCase();
            return email && !localEmailSet.has(email);
        })
        .map(u => ({ ...u, _isM365: true })); // tag so the dropdown can show a hint

    const combinedMentionUsers = [...localMentionUsers, ...uniqueM365Users].slice(0, 10);

    /**
     * Complete the @mention by replacing the partial "@search" with "@DisplayName ".
     * Auto-translates email input to the user's display name.
     */
    const handleUserMention = (user) => {
        const textBeforeCursor = commentText.substring(0, cursorPosition);
        const textAfterCursor  = commentText.substring(cursorPosition);

        // Find the mention-starting @ using the same backwards search as handleTextChange
        let mentionStart = -1;
        for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
            const ch = textBeforeCursor[i];
            if (ch === ' ' || ch === '\n') break;
            if (ch === '@') {
                const charBefore = i > 0 ? textBeforeCursor[i - 1] : ' ';
                if (charBefore === ' ' || charBefore === '\n' || i === 0) {
                    mentionStart = i;
                }
                break;
            }
        }

        if (mentionStart !== -1) {
            const userMention = `@${getUserDisplay(user)}`;
            const newText = commentText.substring(0, mentionStart) + userMention + ' ' + textAfterCursor;

            onCommentChange({ target: { value: newText } });
            setShowMentionSuggestions(false);

            setTimeout(() => {
                if (textareaRef.current) {
                    const newCursorPos = mentionStart + userMention.length + 1;
                    textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                    textareaRef.current.focus();
                }
            }, 0);
        }
    };

    /**
     * Close the mention dropdown when the user clicks elsewhere.
     */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target) &&
                textareaRef.current &&
                !textareaRef.current.contains(event.target)
            ) {
                setShowMentionSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /**
     * Dismiss the mention dropdown on Escape.
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && showMentionSuggestions) {
            setShowMentionSuggestions(false);
            e.preventDefault();
        }
    };

    // ----------------------------------------------------------------
    // Render
    // ----------------------------------------------------------------

    return (
        <div className='comment-composer'>

            {/* ── Header: Write / Preview mode toggle ── */}
            <div className='comment-composer-header'>
                <div className='comment-mode-toggle'>
                    <button
                        type='button'
                        className={`comment-mode-btn ${commentMode === 'write' ? 'active' : ''}`}
                        onClick={() => setCommentMode('write')}
                        title='Write mode'
                    >
                        <FaPen size={11} /> Write
                    </button>
                    <button
                        type='button'
                        className={`comment-mode-btn ${commentMode === 'preview' ? 'active' : ''}`}
                        onClick={() => setCommentMode('preview')}
                        disabled={!commentText.trim()}
                        title='Preview rendered markdown'
                    >
                        <FaEye size={11} /> Preview
                    </button>
                </div>
                <span className='comment-md-hint'>Markdown supported</span>
            </div>

            {/* ── Markdown toolbar — only shown in write mode ── */}
            {commentMode === 'write' && (
                <div className='comment-md-toolbar'>
                    {TOOLBAR_BUTTONS.map((btn, i) =>
                        btn.sep
                            ? <span key={i} className='comment-md-toolbar__sep' />
                            : (
                                <button
                                    key={i}
                                    type='button'
                                    className='comment-md-toolbar__btn'
                                    title={btn.title}
                                    /* mouseDown prevents the textarea losing focus before we read selectionStart/End */
                                    onMouseDown={(e) => { e.preventDefault(); handleToolbarAction(btn); }}
                                >
                                    {btn.icon}
                                </button>
                            )
                    )}
                </div>
            )}

            {/* ── Write mode: textarea with @mention dropdown ── */}
            {commentMode === 'write' && (
                <div
                    className={`comment-editor-wrapper ${isDragOver ? 'drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <textarea
                        ref={textareaRef}
                        value={commentText}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder={onFileAttach ? 'Add a progress update… (drag & drop files, @mention users, Markdown supported)' : 'Add a progress update… (@mention users, Markdown supported)'}
                        rows={5}
                        disabled={isSubmitting}
                        className='comment-textarea'
                    />
                    {isDragOver && (
                        <div className='drop-overlay'>
                            <FaPaperclip size={24} />
                            <span>Drop files to attach</span>
                        </div>
                    )}

                    {/* @mention suggestions dropdown.
                        Shows when the mention trigger is active and either:
                        - Local user results exist, OR
                        - An M365 search is in progress (loading state), OR
                        - M365 results have been returned
                    */}
                    {showMentionSuggestions && (combinedMentionUsers.length > 0 || isSearchingM365) && (
                        <div
                            ref={suggestionsRef}
                            className='mention-suggestions-dropdown'
                        >
                            {combinedMentionUsers.map((user, index) => {
                                // Build a stable React key:
                                // - Local users have a numeric userID or id
                                // - M365 users have a GUID id
                                // - Always fall back to email which is always present for Entra users
                                // - Prefix M365 entries to prevent key collisions with local IDs
                                const email = user.email || user.mail || '';
                                const userId = user._isM365
                                    ? `m365-${user.id || email || index}`
                                    : (user.userID ?? user.id ?? email ?? `local-${index}`);
                                return (
                                    <div
                                        key={userId}
                                        className='mention-suggestion-item'
                                        onClick={() => handleUserMention(user)}
                                    >
                                        <span className='mention-suggestion-name'>{getUserDisplay(user)}</span>
                                        {email && (
                                            <span className='mention-suggestion-email'>{email}</span>
                                        )}
                                        {/* Visual tag for Microsoft 365 users not in the local system */}
                                        {user._isM365 && (
                                            <span className='mention-suggestion-m365-tag'>M365</span>
                                        )}
                                    </div>
                                );
                            })}
                            {/* Loading indicator shown while the M365 search request is in flight */}
                            {isSearchingM365 && (
                                <div className='mention-suggestion-loading'>
                                    Searching Microsoft 365…
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Inline image previews (in write mode) ── */}
            {commentMode === 'write' && (() => {
                const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
                const images = [];
                let match;
                while ((match = imageRegex.exec(commentText)) !== null) {
                    // Parse through the URL constructor to break the taint flow from user text.
                    // Using parsedUrl.href (browser-normalized) instead of the raw extracted string
                    // satisfies CodeQL js/xss-through-dom: the src attribute receives a URL object
                    // output, not a raw user string. Only http(s) schemes are permitted.
                    try {
                        const parsedUrl = new URL(match[2]);
                        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
                            images.push({ alt: match[1], url: parsedUrl.href });
                        }
                    } catch {
                        // Invalid or non-parseable URL — skip this image preview
                    }
                }
                return images.length > 0 ? (
                    <div className='comment-image-previews'>
                        {images.map((img, i) => (
                            <div key={i} className='comment-image-preview-item'>
                                <img src={img.url} alt={img.alt} />
                                <span className='comment-image-preview-label'>{img.alt || 'Image'}</span>
                            </div>
                        ))}
                    </div>
                ) : null;
            })()}

            {/* ── Preview mode: rendered markdown ── */}
            {commentMode === 'preview' && (
                <div className='comment-preview-panel'>
                    {commentText.trim() ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {commentText}
                        </ReactMarkdown>
                    ) : (
                        <p className='comment-preview-empty'>Nothing to preview yet.</p>
                    )}
                </div>
            )}

            {/* ── Submit row ── */}
            <div className='comment-btn-row'>
                {onFileAttach && (
                    <>
                        <button
                            type='button'
                            className='comment-attach-btn'
                            onClick={() => fileInputRef.current?.click()}
                            title='Attach file'
                        >
                            <FaPaperclip /> Attach
                        </button>
                        <input
                            ref={fileInputRef}
                            type='file'
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const files = Array.from(e.target.files);
                                if (files.length > 0) handleFilesAttach(files);
                                e.target.value = '';
                            }}
                        />
                    </>
                )}

                {/* Split button: "Add Comment" with optional "Add Internal Note" dropdown */}
                <div className='comment-submit-split-btn'>
                    <button
                        type='button'
                        onClick={onCommentSubmit}
                        disabled={isSubmitting || !commentText.trim()}
                        className='comment-submit-main'
                    >
                        {isSubmitting ? 'Submitting…' : 'Add Comment'}
                    </button>
                    {canAddInternalNotes && onInternalNoteSubmit && (
                        <button
                            type='button'
                            className='comment-submit-dropdown-toggle'
                            disabled={isSubmitting || !commentText.trim()}
                            onClick={onInternalNoteSubmit}
                            title='Add as Internal Note (visible only to permitted staff)'
                        >
                            <FaLock /> Internal Note
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommentInputSection;
